import { useGET, usePOST, usePUT } from "hooks/useAPI";

/**
 * Viewer's case-contact record on this case. Powers the
 * "viewer is the purchaser" branch the sign + payment screens need.
 */
export function useCaseViewer(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}/viewer` : null);
}

/**
 * Purchaser-role contact resolved server-side. Returns the masked record
 * (name + first-letter-only email/phone for non-purchaser viewers).
 */
export function useCasePurchaser(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}/purchaser` : null);
}

/**
 * The masked case record. Same SWR key as the route guard's own read, so the
 * two share one request. The death-certificate step reads it for the fields
 * that decide whether the ordering question is still open
 * (`CaseDeathCertificateState`), and needs `mutate` to revalidate when the
 * server reports the order was placed mid-session.
 */
export function useCase(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}` : null);
}

/**
 * Record who orders the death certificates on the case. Idempotent property
 * set; conflicts arrive as a 409 carrying an `error_code`, of which only
 * `dc_ordering_preference_locked` is recoverable in place — After has already
 * ordered and the choice can no longer change.
 */
export function useSetDcOrderingPreference(caseId: string | undefined) {
  return usePUT(
    caseId ? `/cases/${caseId}/arrangement/dc-ordering-preference` : "",
  );
}

/** Slim case-context + arrangement_status. The heavy config bundles moved
 *  to /arrangement/pricing and /arrangement/catalog. */
export function useArrangementConfig(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}/arrangement/config` : null);
}

/**
 * Case-scoped pricing data: packages, add-ons, add-on groups, service
 * questions, and per-package inclusions. Cacheable upstream (Cache-Control
 * private, max-age 300).
 */
export function useArrangementPricing(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}/arrangement/pricing` : null);
}

/**
 * Shopify urn + keepsake catalog for the case's market. Cacheable upstream
 * (Cache-Control private, max-age 300).
 */
export function useArrangementCatalog(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}/arrangement/catalog` : null);
}

/** Load persisted arrangement selections (snake_case strict-allowlist body) */
export function useArrangementSelections(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}/arrangement/selections` : null);
}

/** Save/upsert arrangement selections (snake_case strict-allowlist body) */
export function useSaveArrangementSelections(caseId: string | undefined) {
  return usePUT(caseId ? `/cases/${caseId}/arrangement/selections` : "");
}

/** Recalculate quote based on current selections */
export function useCalculateQuote(caseId: string | undefined) {
  return usePOST(caseId ? `/cases/${caseId}/arrangement/quote` : "");
}

/**
 * Confirm the current selections. Transactional kickoff for async PandaDoc
 * document creation — sets `selections_confirmed_by` and enqueues the
 * Cloud Tasks creation job.
 */
export function useConfirmSelections(caseId: string | undefined) {
  return usePOST(
    caseId ? `/cases/${caseId}/arrangement/confirm-selections` : "",
  );
}

/**
 * Arrangement-document state: the async-creation lifecycle fields plus a live
 * doc-service read (status + recipients). Pass a non-zero `refreshInterval` to
 * poll — the sign screen polls at 1s while the document is being created and
 * at 500ms while verifying a completed signature.
 */
export function useArrangementDocument(
  caseId: string | undefined,
  refreshInterval = 0,
) {
  return useGET(
    caseId ? `/cases/${caseId}/arrangement/document` : null,
    undefined,
    {
      refreshInterval,
    },
  );
}

/**
 * Mint (or reuse) the viewer's PandaDoc embedded-signing session. The API
 * gates this on document readiness and, for non-purchaser signers, a recorded
 * attestation — so callers must POST /arrangement/attestation first when
 * acting on the purchaser's behalf.
 */
export function useDocumentSession(caseId: string | undefined) {
  return usePOST(caseId ? `/cases/${caseId}/arrangement/document/session` : "");
}

/**
 * Record that the viewer is continuing on the purchaser's behalf. One
 * attestation authorizes both signing and payment for the current arrangement.
 */
export function useArrangementAttestation(caseId: string | undefined) {
  return usePOST(caseId ? `/cases/${caseId}/arrangement/attestation` : "");
}

/** Fetch existing payment methods for the case's purchaser */
export function usePaymentMethods(caseId: string | undefined) {
  return useGET(caseId ? `/cases/${caseId}/arrangement/payment-methods` : null);
}

/** Create a Stripe Setup Intent for new card entry */
export function useCreateSetupIntent(caseId: string | undefined) {
  return usePOST(
    caseId ? `/cases/${caseId}/arrangement/payment-methods/setup-intent` : "",
  );
}

/**
 * Persist a payment method after Stripe card capture. Body: { payment_method
 * (pm_…), last_4?, type? } — billing details go to Stripe only, never here.
 * Returns the saved record ({ record_id, stripe_id, type, last_4 }).
 */
export function useConfirmPaymentMethod(caseId: string | undefined) {
  return usePOST(caseId ? `/cases/${caseId}/arrangement/payment-methods` : "");
}

/**
 * Confirm the arrangement payment with the selected PM's record_id. Finalizes
 * the arrangement (prereqs: selections confirmed + purchaser signed).
 */
export function useConfirmPayment(caseId: string | undefined) {
  return usePOST(caseId ? `/cases/${caseId}/arrangement/confirm-payment` : "");
}
